import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import { CuttingProduct, CuttingSettings, OverlayLoaderModule, PriceListProductBazisElement } from 'cloud-shared-lib';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, concatMap, finalize, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { OrderDetailService } from '../../../order-detail.service';
import { ProductElementsComponent } from '../../add-product-dialog/product-detail/product-elements/product-elements.component';
import { ProductDataFormComponent } from '../product-data-form/product-data-form.component';

@Component({
  selector: 'cutting-edit-product-dialog',
  templateUrl: './edit-product-dialog.component.html',
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    ProductDataFormComponent,
    ProductElementsComponent,
    OverlayLoaderModule,
    MatSnackBarModule,
  ],
})
export class EditProductDialogComponent implements OnDestroy, OnInit {
  @ViewChild('productDataForm', { static: true }) productDataForm: ProductDataFormComponent;

  cuttingOptions: CuttingSettings;
  originalElementList: PriceListProductBazisElement[];
  set originalElements(val: PriceListProductBazisElement[]) {
    this.originalElementList = _.cloneDeep(val);
  }
  onElementsChanged: BehaviorSubject<PriceListProductBazisElement[]> = new BehaviorSubject([]);
  save$: Observable<void>;
  unsubscribe$: Subject<void> = new Subject();
  loading$: Observable<boolean> = of(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public product: CuttingProduct,
    public dialogRef: MatDialogRef<EditProductDialogComponent>,
    private orderDetailService: OrderDetailService,
    private snackBar: MatSnackBar,
    private userSettingsService: UserSettingsService
  ) {}

  ngOnInit(): void {
    this.originalElements = this.product.elements;
    this.getCuttingSettings();
  }

  saveAndClose(): void {
    this.saveCuttingProduct();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private saveCuttingProduct(): void {
    const newProduct = _.cloneDeep(this.product);

    newProduct.count = this.productDataForm.productForm.controls.count.value;
    newProduct.width = this.productDataForm.productForm.controls.width.value;
    newProduct.height = this.productDataForm.productForm.controls.height.value;
    newProduct.deep = this.productDataForm.productForm.controls.deep.value;

    newProduct.elements = _.isEmpty(this.onElementsChanged.value)
      ? this.originalElementList
      : this.onElementsChanged.value;

    _.each(newProduct.elements, element => {
      if (element.edgeList && element.edgeList.some(e => _.isEmpty(e.name) || _.isNil(e.name))) {
        this.snackBar.open($localize`Необходимо назначить облицовку для элементов`);
        return;
      }
    });

    this.save$ = this.orderDetailService.saveCuttingProduct(newProduct, this.product.id).pipe(
      concatMap(p => {
        this.loading$ = of(true);
        return this.orderDetailService.cuttingProductChangeMaterials(p);
      }),
      finalize(() => {
        this.loading$ = of(false);
        this.dialogRef.close(true);
      }),
      catchError(() => {
        this.snackBar.open($localize`Не удалось сохранить изделие`);
        this.loading$ = of(false);
        this.dialogRef.close(false);
        return of(null);
      })
    );
  }

  private getCuttingSettings(): void {
    this.userSettingsService
      .getCuttingSettings()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(o => {
        this.cuttingOptions = o;
      });
  }
}
